import React, { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import "../../Merchant/EditProfileForm/editprofileform.scss";
import BreadCrumsHeader from "../../common/BreadCrums";
import FormLayout from "../../common/FormLayout";
import PersonIcon from "../../../Assets/Images/Person.svg";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { updatePasswordSchema, editProfileSchema } from "../../../utils/validationSchemas";
import Loader from "../../common/Loader";
import EditProfile from "./EditProfile";
import { getFundraiserProfile, updateFundraiserProfile, getFundraiserCategories, getSubCategoriesByCategoryId } from "../../../services/fundraiserService";
import { getReasons } from "../../../services/onboardingService";
import { updatePassword } from "../../../services/authService";
import UpdatePassword from "../../Merchant/EditProfileForm/UpdatePassword";
import BankAccountDetails from "../../Fundraiser/BanksList/BankAccountDetails";
import toast from "../../../utils/toast";


const FundraiserEditProfileFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.franchiseReducer?.loading);
  const [uploadClicked, setUploadClicked] = useState(false);
  const [profileData, setProfileData] = useState();
  const [isUpdatePassword, setIsUpdatePassword] = useState(false);
  const userData = useSelector((state) => state.authReducer.tokenData.user);
  const { id = null } = location?.state || "";

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const format = "hh:mm A";

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [allReasons, setAllReasons] = useState([]);
  const [customReasonId, setCustomReasonId] = useState(null);
  const lastSubmittedRef = useRef({});
  const previousCategoryRef = useRef(null);
  const hasNormalizedSubsRef = useRef(false);
  const initialSubCategoryIdsRef = useRef([]);

  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return html;
    return html
      .replace(/<[^>]*>/g, "") // remove tags
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .trim();
  };

  useEffect(() => {
    dispatch(getFundraiserCategories())
      .unwrap()
      .then((res) => {
        if (res?.success && res?.data) setCategories(res.data);
      });
    dispatch(getReasons())
      .unwrap()
      .then((res) => {
        if (res?.success && res?.data) setAllReasons(res.data);
      });
  }, [dispatch]);

  useEffect(() => {
    if (profileData && profileData.category && profileData.category.length > 0) {
      const selectedCat = profileData.category[0];
      if (selectedCat?._id) {
        // Initialize previous category on first profile load
        if (!previousCategoryRef.current) {
          previousCategoryRef.current = selectedCat._id;
        }
        dispatch(getSubCategoriesByCategoryId({ id: selectedCat._id }))
          .unwrap()
          .then((res) => {
            if (res?.success) {
              const apiSubs = Array.isArray(res?.data) ? res.data : [];
              if (apiSubs.length > 0) {
                setSubCategories(apiSubs);
              } else {
                // Fallback: if API returns empty (e.g., Sporting Clubs), use existing profile subCategory only
                const fallback = Array.isArray(profileData?.subCategory || profileData?.subCategories)
                  ? (profileData?.subCategory || profileData?.subCategories).map((s) => ({
                      _id: s?._id || s,
                      title: s?.title || s?.name || s,
                    })).filter((s) => s._id && s.title)
                  : [];
                setSubCategories(fallback);
              }
            }
          });
      }
    }
  }, [dispatch, profileData]);

  const getCategoryIdByTitle = (title) => {
    if (!title || (typeof title === 'string' && title.trim() === '')) return null;
    const cat = categories.find((c) => c.title === title || c._id === title);
    return cat ? cat._id : (typeof title === 'string' && title.length === 24 && /^[0-9a-fA-F]+$/.test(title) ? title : null);
  };

  const getSubCategoryIdsByTitles = (titles) => {
    return (titles || [])
      .map((title) => {
        if (typeof title === 'string' && title.length === 24 && /^[0-9a-fA-F]+$/.test(title)) {
          return title;
        }
        const sub = subCategories.find((s) => s.title === title || s._id === title);
        return sub ? sub._id : null;
      })
      .filter((id) => typeof id === 'string' && id.length === 24 && /^[0-9a-fA-F]+$/.test(id));
  };

  const getReasonObjectsByStrings = (reasons) => {
    return (reasons || []).map((reason) => {
      if (typeof reason === 'string' && reason.length === 24 && /^[0-9a-fA-F]+$/.test(reason)) {
        return { _id: reason };
      }
      const found = allReasons.find((r) => r.reason === reason || r._id === reason);
      return found ? { _id: found._id } : null;
    }).filter(Boolean);
  };



  const initialValues = isUpdatePassword
    ? {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }
    : {
        logo: profileData?.logo || profileData?.logoUrl || profileData?.user?.logo || "",
        businessName: profileData?.fundraiserName || profileData?.user?.name || profileData?.name || "",
        email: profileData?.user?.email || "",
        contact: profileData?.contactPersonNumber || "",
        address: profileData?.address || "",
        websiteUrl: profileData?.website || "",
        personContact: profileData?.contactPersonName || "",
        aboutBusiness: stripHtml(profileData?.description) || "",
        fundraiserCause: profileData?.reason ? profileData.reason.map(r => {
          // Prefer IDs for select values; fallback to string
          if (typeof r === 'string' && r.length === 24 && /^[0-9a-fA-F]+$/.test(r)) {
            return r;
          }
          return r?._id || (typeof r === 'string' ? r : r?.reason);
        }) : [],
        fundraiserCategory: (profileData?.category || profileData?.categories) && Array.isArray((profileData?.category || profileData?.categories)) && (profileData?.category || profileData?.categories).length > 0 ? ((profileData?.category || profileData?.categories)[0].title || (profileData?.category || profileData?.categories)[0]._id || "") : "",
        fundraiserSubCategory: (profileData?.subCategory || profileData?.subCategories) && Array.isArray(profileData?.subCategory || profileData?.subCategories) ? (profileData?.subCategory || profileData?.subCategories).map(sub => {
          let v;
          if (typeof sub === 'string' && sub.length === 24 && /^[0-9a-fA-F]+$/.test(sub)) {
            v = sub;
          } else {
            v = sub?._id || sub?.title || "";
          }
          return String(v);
        }) : [],
        organisationName: profileData?.organizationName || "",
        abn: profileData?.abnNumber || "",
        charityRegistrationNumber: profileData?.charityRegistrationNumber || "",
        accountsEmail: profileData?.accountEmail || "",
      };

  const [dynamicSchema, setDynamicSchema] = useState(editProfileSchema);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: isUpdatePassword ? updatePasswordSchema : dynamicSchema,
    onSubmit: async (values) => {
      try {
        if (isUpdatePassword) {
          const passwordPayload = {
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            confirmPassword: values.confirmPassword,
          };
          const resultAction = await dispatch(updatePassword(passwordPayload));
          if (resultAction?.payload?.success) {
            setIsUpdatePassword(false);
            formik.setValues({ currentPassword: "", newPassword: "", confirmPassword: "" });
            formik.setTouched({});
          } else {
            const message = resultAction?.payload?.message || resultAction?.error?.message || "Failed to update password";
            // eslint-disable-next-line no-console
            console.error("Update password failed:", { message, resultAction });
          }
          return;
        }

        if (values.fundraiserCause && values.fundraiserCause.includes("Other")) {
          return;
        }

        const payload = {};
        const addIfTruthy = (key, val) => {
          if (val === undefined || val === null) return;
          if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed === '') return;
            payload[key] = trimmed;
            return;
          }
          if (Array.isArray(val) && val.length === 0) return;
          payload[key] = val;
        };

        addIfTruthy('fundraiserName', values.businessName);
        addIfTruthy('description', values.aboutBusiness);
        addIfTruthy('logo', values.logo);
        addIfTruthy('website', values.websiteUrl);
        addIfTruthy('contactPersonName', values.personContact);
        addIfTruthy('contactPersonNumber', values.contact);
        addIfTruthy('address', values.address);

        const resolvedCategoryId = getCategoryIdByTitle(values.fundraiserCategory);
        if (resolvedCategoryId) {
          payload.category = resolvedCategoryId;
        }

        if (values.fundraiserCategory !== 'Community Organisations') {
          const resolvedSubIds = getSubCategoryIdsByTitles(values.fundraiserSubCategory);
          if (resolvedSubIds && resolvedSubIds.length > 0) {
            payload.subCategory = resolvedSubIds;
          }
        } else {
          // Explicitly clear sub categories if category demands it
          payload.subCategory = [];
        }

        const resolvedReasons = (() => {
          const reasonObjects = getReasonObjectsByStrings(values.fundraiserCause);
          if (customReasonId && !reasonObjects.some(r => r._id === customReasonId)) {
            reasonObjects.push({ _id: customReasonId });
          }
          return reasonObjects.filter((r) => r && r._id);
        })();
        if (resolvedReasons && resolvedReasons.length > 0) {
          payload.reason = resolvedReasons;
        }

        addIfTruthy('organizationName', values.organisationName);
        addIfTruthy('abnNumber', values.abn);
        addIfTruthy('charityRegistrationNumber', values.charityRegistrationNumber);
        addIfTruthy('accountEmail', values.accountsEmail);
        // Persist logo updates including explicit clear (empty string)
        if (values.logo === "") {
          payload.logo = ""; // explicit clear request
        } else {
          addIfTruthy('logo', values.logo);
        }
        // Keep a snapshot of values we send to prevent UI from clearing if API returns null
        lastSubmittedRef.current = {
          ...lastSubmittedRef.current,
          accountEmail: values.accountsEmail ? values.accountsEmail.trim() : undefined,
        };
        
        console.log('Sending payload to API:', payload);
        console.log('accountEmail being sent:', payload.accountEmail);
        
        const result = await updateFundraiserProfile(payload);
        console.log('API response:', result);
        
        if (result?.success) {
          toast.success("Profile updated successfully");
          // Keep a snapshot for fields the API may return stale immediately after write
          lastSubmittedRef.current = {
            ...lastSubmittedRef.current,
            accountEmail: values.accountsEmail ? values.accountsEmail.trim() : undefined,
            charityRegistrationNumber: values.charityRegistrationNumber ? values.charityRegistrationNumber.trim() : undefined,
            fundraiserName: values.businessName ? values.businessName.trim() : undefined,
          };

          // Optimistically reflect UI with latest inputs
          setProfileData((prev) => ({
            ...(prev || {}),
            accountEmail: lastSubmittedRef.current.accountEmail ?? prev?.accountEmail ?? '',
            charityRegistrationNumber: lastSubmittedRef.current.charityRegistrationNumber ?? prev?.charityRegistrationNumber ?? '',
            fundraiserName: lastSubmittedRef.current.fundraiserName ?? prev?.fundraiserName ?? '',
            // If user cleared the logo, keep it cleared instead of falling back
            logo: (values.logo === "") ? "" : (values.logo || prev?.logo || ''),
            // Preserve sub-categories from form values to prevent empty flash (keep as IDs for Selector matching)
            subCategory: values.fundraiserSubCategory ? values.fundraiserSubCategory.map((val) => {
              // If it's already an ID, use it
              if (typeof val === 'string' && /^[0-9a-fA-F]{24}$/.test(val)) {
                const found = subCategories.find((s) => s._id === val);
                return found ? { _id: found._id, title: found.title } : { _id: val, title: val };
              }
              // Otherwise it's a title, find the matching sub-category
              const found = subCategories.find((s) => s.title === val || s._id === val);
              return found ? { _id: found._id, title: found.title } : { _id: val, title: val };
            }) : prev?.subCategory || [],
            user: {
              ...(prev?.user || {}),
              name: lastSubmittedRef.current.fundraiserName ?? prev?.user?.name ?? '',
            },
          }));

          // Re-fetch to sync other fields from server
          getProfile();
          setCustomReasonId(null);
          
          // Trigger navbar refresh by dispatching a custom event
          window.dispatchEvent(new Event('profile-updated'));
        } else {
          toast.error(result?.message || "Failed to update profile");
        }
      } catch (error) {
        console.error("Profile update error:", error);
        toast.error(error?.response?.data?.message || error?.message || "Failed to update profile");
      }
    },
  });

  const { handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue, setValues } = formik;

  // Build dynamic schema: enforce fundraiser signup-required fields on edit profile
  useEffect(() => {
    if (isUpdatePassword) return; // password flow uses its own schema

    const requiredSchema = editProfileSchema.shape({
      // Logo is optional on edit profile
      logo: Yup.string(),
      businessName: Yup.string()
        .max(50, "Fundraiser Name cannot be more than 50 characters")
        .matches(/^[A-Za-z\s]+$/, "Fundraiser Name can contain letters and spaces only")
        .required("Fundraiser Name is required"),
      address: Yup.string().required("Fundraiser Address is required"),
      websiteUrl: Yup.string()
        .matches(
          /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}(\/[^\s]*)?$/,
          "Invalid website URL"
        )
        .required("Website URL is required"),
      personContact: Yup.string().required("Contact Person Name is required"),
      contact: Yup.string().required("Contact is required"),
      aboutBusiness: Yup.string()
        .required("Organization Information is required")
        .max(500, "Organization Information cannot be more than 500 characters"),
      fundraiserCategory: Yup.string().required("Fundraiser Category is required"),
      fundraiserSubCategory: Yup.array().when("fundraiserCategory", {
        is: (val) => val && val !== "Community Organisations",
        then: (s) => s.min(1, "Please select at least one sub category"),
        otherwise: (s) => s.optional(),
      }),
      fundraiserCause: Yup.array().min(1, "Please select at least one cause"),
      organisationName: Yup.string()
        .max(100, "Organisation Name cannot be more than 100 characters")
        .required("Organisation Name is required"),
      abn: Yup.string()
        .matches(/^[0-9]{11}$/, "ABN must be an 11-digit number")
        .required("ABN is required"),
      accountsEmail: Yup.string()
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address")
        .required("Accounts Email is required"),
    });

    setDynamicSchema(requiredSchema);
  }, [isUpdatePassword, formik?.values?.fundraiserCategory]);

  useEffect(() => {
    if (formik?.values?.fundraiserCategory && profileData) {
      const categoryId = getCategoryIdByTitle(formik.values.fundraiserCategory);
      if (categoryId && categoryId.length === 24) {
        // Clear subcategories only if the user actually changed the category
        if (previousCategoryRef.current && previousCategoryRef.current !== categoryId) {
          setFieldValue('fundraiserSubCategory', []);
        }
        let isMounted = true;
        dispatch(getSubCategoriesByCategoryId({ id: categoryId }))
          .unwrap()
          .then((res) => {
            if (isMounted && res?.success) {
              const apiSubs = Array.isArray(res?.data) ? res.data : [];
              if (apiSubs.length > 0) {
                setSubCategories(apiSubs);
              } else {
                const fallback = Array.isArray(profileData?.subCategory || profileData?.subCategories)
                  ? (profileData?.subCategory || profileData?.subCategories).map((s) => ({
                      _id: s?._id || s,
                      title: s?.title || s?.name || s,
                    })).filter((s) => s._id && s.title)
                  : [];
                setSubCategories(fallback);
              }
              // Update the previous category after we load the new subcategories
              previousCategoryRef.current = categoryId;
            }
          })
          .catch(() => {});
        return () => {
          isMounted = false;
        };
      }
    }
  }, [formik?.values?.fundraiserCategory]);

  // Normalize sub-category IDs to titles for display once subCategories are loaded (only once, prevent loops)
  useEffect(() => {
    if (subCategories.length > 0 && initialSubCategoryIdsRef.current.length > 0 && !hasNormalizedSubsRef.current) {
      // Convert stored IDs to titles
      const normalized = initialSubCategoryIdsRef.current.map((id) => {
        const found = subCategories.find((s) => s._id === id);
        return found ? found.title : id;
      }).filter((t) => typeof t === 'string' && t.length > 0);
      
      if (normalized.length > 0) {
        hasNormalizedSubsRef.current = true;
        setFieldValue('fundraiserSubCategory', normalized, false);
        // Clear stored IDs after normalization
        initialSubCategoryIdsRef.current = [];
      } else {
        hasNormalizedSubsRef.current = true;
      }
    } else if (subCategories.length > 0 && initialSubCategoryIdsRef.current.length === 0) {
      // No IDs to normalize, mark as done
      hasNormalizedSubsRef.current = true;
    }
  }, [subCategories, setFieldValue]);

  // Reset normalization flag and stored IDs when profileData changes
  useEffect(() => {
    hasNormalizedSubsRef.current = false;
    initialSubCategoryIdsRef.current = [];
  }, [profileData]);

  // Removed duplicate clearing effect; handled in the fetch effect above

  useEffect(() => {
    if (profileData) {
      const finalEmail = (lastSubmittedRef.current.accountEmail !== undefined)
        ? lastSubmittedRef.current.accountEmail
        : (profileData?.accountEmail || "");
      const finalCharity = (lastSubmittedRef.current.charityRegistrationNumber !== undefined)
        ? lastSubmittedRef.current.charityRegistrationNumber
        : (profileData?.charityRegistrationNumber || "");
      const finalFundraiserName = (lastSubmittedRef.current.fundraiserName !== undefined)
        ? lastSubmittedRef.current.fundraiserName
        : (profileData?.fundraiserName || profileData?.user?.name || "");
      
      console.log('Setting form values with accountEmail:', {
        profileDataEmail: profileData?.accountEmail,
        lastSubmittedEmail: lastSubmittedRef.current.accountEmail,
        finalEmail
      });
      
      setValues({
        logo: profileData?.logo || profileData?.logoUrl || profileData?.user?.logo || "",
        businessName: finalFundraiserName,
        email: profileData?.user?.email || "",
        contact: profileData?.contactPersonNumber || "",
        address: profileData?.address || "",
        websiteUrl: profileData?.website || "",
        personContact: profileData?.contactPersonName || "",
        aboutBusiness: stripHtml(profileData?.description) || "",
        fundraiserCause: profileData?.reason ? profileData.reason.map(r => {
          if (typeof r === 'string' && r.length === 24 && /^[0-9a-fA-F]+$/.test(r)) {
            return r;
          }
          return typeof r === 'string' ? r : r.reason || r._id;
        }) : [],
        fundraiserCategory: (profileData?.category || profileData?.categories) && Array.isArray((profileData?.category || profileData?.categories)) && (profileData?.category || profileData?.categories).length > 0 ? ((profileData?.category || profileData?.categories)[0].title || (profileData?.category || profileData?.categories)[0]._id || "") : "",
        // Pre-populate from profile; store IDs initially, will be converted to titles when subCategories load
        fundraiserSubCategory: (() => {
          const subs = Array.isArray(profileData?.subCategory || profileData?.subCategories)
            ? (profileData?.subCategory || profileData?.subCategories).map((sub) => {
                if (typeof sub === 'string') {
                  if (/^[0-9a-fA-F]{24}$/.test(sub)) return sub;
                  return sub;
                }
                return sub?._id || sub?.title || "";
              }).filter(Boolean)
            : [];
          // Store IDs for later normalization
          initialSubCategoryIdsRef.current = subs.filter((s) => typeof s === 'string' && /^[0-9a-fA-F]{24}$/.test(s));
          return subs;
        })(),
        organisationName: profileData?.organizationName || "",
        abn: profileData?.abnNumber || "",
        charityRegistrationNumber: finalCharity,
        accountsEmail: finalEmail,
      });
    }
  }, [profileData, setValues]);

  const items = [
    { title: "Profile" },
    { title: "Edit Profile" },
  ];

  const [placeId, setPlaceId] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const handleCancelClick = () => {
    const resetValues = { ...formik.values };
    Object.keys(formik.touched).forEach((key) => {
      if (key === 'aboutBusiness' && formik.initialValues.hasOwnProperty('aboutBusiness')) {
        resetValues['aboutBusiness'] = formik.initialValues['aboutBusiness'];
      } else if (key === 'aboutUs' && formik.initialValues.hasOwnProperty('aboutUs')) {
        resetValues['aboutUs'] = formik.initialValues['aboutUs'];
      } else {
        resetValues[key] = formik.initialValues[key];
      }
    });
    formik.setValues(resetValues);
    formik.setTouched({});
    setResetKey(prev => prev + 1);
    

  };

  const getProfile = async () => {
    try {
      const fundraiserProfile = await getFundraiserProfile();
      if (fundraiserProfile?.success === true) {
        const merged = { ...(fundraiserProfile?.data || {}) };
        // If API still returns stale values shortly after save, prefer last submitted
        if (lastSubmittedRef.current.accountEmail !== undefined) {
          merged.accountEmail = lastSubmittedRef.current.accountEmail;
        }
        if (lastSubmittedRef.current.charityRegistrationNumber !== undefined) {
          merged.charityRegistrationNumber = lastSubmittedRef.current.charityRegistrationNumber;
        }
        if (lastSubmittedRef.current.fundraiserName !== undefined) {
          merged.fundraiserName = lastSubmittedRef.current.fundraiserName;
          merged.user = { ...(merged.user || {}), name: lastSubmittedRef.current.fundraiserName };
        }
        // Preserve sub-categories from current profileData if they exist (to prevent empty flash)
        if (profileData?.subCategory && Array.isArray(profileData.subCategory) && profileData.subCategory.length > 0) {
          merged.subCategory = profileData.subCategory;
        } else if (profileData?.subCategories && Array.isArray(profileData.subCategories) && profileData.subCategories.length > 0) {
          merged.subCategories = profileData.subCategories;
        }
        setProfileData(merged);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch fundraiser profile", error);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  const handleUpdateClick = () => {
    setIsUpdatePassword(true);
  };

  const handleOtherInfoClick = () => {
    setIsUpdatePassword(false);
  };

  const handleCustomReasonCreated = (reasonId) => {
    setCustomReasonId(reasonId);
  };

  const scrollToFirstError = (formErrors) => {
    const priority = [
      'fundraiserCategory',
      'fundraiserSubCategory',
      'fundraiserCause',
      'businessName',
      'websiteUrl',
      'contact',
      'address',
      'aboutBusiness',
      'logo',
      'personContact',
      'email',
      'organisationName',
      'abn',
      'charityRegistrationNumber',
      'accountsEmail',
    ];
    let targetField = priority.find((key) => formErrors && Object.prototype.hasOwnProperty.call(formErrors, key));
    if (!targetField) {
      const keys = Object.keys(formErrors || {});
      targetField = keys.length ? keys[0] : null;
    }
    if (!targetField) return;
    setTimeout(() => {
      try {
        const el = document.querySelector(`[data-field="${targetField}"]`) || document.querySelector(`[name="${targetField}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (_) {}
    }, 0);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors || {}).length > 0) {
      formik.setTouched(Object.keys(validationErrors).reduce((acc, k) => { acc[k] = true; return acc; }, {}), true);
      scrollToFirstError(validationErrors);
      return;
    }
    handleSubmit(e);
  };

  return (
    <div className="edit-profile-container">
      {loading === true && <Loader />}
      <BreadCrumsHeader heading="Edit Profile" items={items} form={true} />
      <FormLayout
        title={"Profile Information"}
        content={
          <form onSubmit={handleFormSubmit} className="px-5">
            <div className="mt-5 w-full flex justify-between items-center">
              {isUpdatePassword ? (
                <p
                  className="cursor-pointer font-poppins font-[400] sm:text-[12px] md:text-[14px] text-[#00ADEF] text-center leading-[18px]"
                  onClick={handleOtherInfoClick}
                >
                  Update Profile Information
                </p>
              ) : (
                <p
                  className="cursor-pointer font-poppins font-[400] sm:text-[12px] md:text-[14px] text-[#00ADEF] text-center leading-[18px]"
                  onClick={handleUpdateClick}
                >
                  Update Password
                </p>
              )}
            </div>
            {isUpdatePassword ? (
              <UpdatePassword
                values={formik.values}
                errors={errors}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
            ) : (
              <>
                <EditProfile
                  values={formik.values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setPlaceId={setPlaceId}
                  setLogoLoading={setLogoLoading}
                  logoLoading={logoLoading}
                  resetKey={resetKey}
                  onCustomReasonCreated={handleCustomReasonCreated}
                />
                <BankAccountDetails />
              </>
            )}
            <div className="col-span-12 text-end mt-14">
              <button
                className="w-[30%] lg:w-[15%] h-[45px] rounded-[5px] bg-[#F0F0F0] text-[16px] font-[500] text-[#8C8C8C]"
                style={{ border: "1px solid #C4C4C4" }}
                onClick={handleCancelClick}
                type="button"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`w-[30%] lg:w-[15%] h-[45px] rounded-[5px] bg-[#00ADEF] text-[16px] font-[500] text-[#FFFFFF] ms-2 ${logoLoading ? 'cursor-not-allowed opacity-60' : ''}`}
                disabled={logoLoading}
              >
                Save
              </button>
            </div>
          </form>
        }
      />
    </div>
  );
};

export default FundraiserEditProfileFormPage;


